
import { useState, useEffect, useCallback } from "react";
import { ContentItem } from "../../content-card/types";
import { GraphData, BoundingBox } from "./hooks/useGraphTypes";
import { useCollectionData } from "./hooks/useCollectionData";
import { useTagData } from "./hooks/useTagData";
import { useGraphNodeBuilder } from "./hooks/useGraphNodeBuilder";
import { useGraphLinkBuilder } from "./hooks/useGraphLinkBuilder";
import { useGraphFiltering } from "./hooks/useGraphFiltering";
import { useGraphMetrics } from "./hooks/useGraphMetrics";

export * from "./hooks/useGraphTypes";

export const useGraphData = (
  contentItems: ContentItem[],
  selectedNode: string | null,
  collectionLinkOpacity: number,
  tagLinkOpacity: number
) => {
  const [graphData, setGraphData] = useState<GraphData>({ nodes: [], links: [] });
  const [allNodes, setAllNodes] = useState<GraphData["nodes"]>([]);
  const [allLinks, setAllLinks] = useState<GraphData["links"]>([]);
  const [boundingBox, setBoundingBox] = useState<BoundingBox | null>(null);
  
  // Fetch collections and tags data
  const { collectionColorMap, contentToCollectionMap } = useCollectionData();
  const { tagColorMap, contentToTagsMap } = useTagData();
  
  // Node and link building functionality
  const { buildGraphNodes } = useGraphNodeBuilder();
  const { buildGraphLinks } = useGraphLinkBuilder();
  
  // Graph filtering and updates
  const { updateLinkOpacity } = useGraphFiltering();
  
  // Generate graph data from content items
  useEffect(() => {
    if (contentItems.length === 0) return;
    
    // Log some debug info about the content items
    console.log(`[COLOR DEBUG] useGraphData processing ${contentItems.length} items:`);
    contentItems.forEach(item => {
      console.log(`Item ${item.id} (${item.title}): collection_color=${item.collection_color}, collection_id=${item.collection_id}`);
    });
    
    const nodes = buildGraphNodes(
      contentItems,
      contentToCollectionMap,
      collectionColorMap,
      null // No more selected node
    );
    
    setAllNodes(nodes);

    const links = buildGraphLinks(
      contentItems,
      contentToCollectionMap,
      collectionColorMap,
      contentToTagsMap,
      tagColorMap,
      collectionLinkOpacity,
      tagLinkOpacity
    );
    
    setAllLinks(links);
  }, [
    contentItems,
    contentToCollectionMap,
    collectionColorMap,
    contentToTagsMap,
    tagColorMap,
    collectionLinkOpacity,
    tagLinkOpacity,
    buildGraphNodes,
    buildGraphLinks
  ]);

  // Update graph data when nodes, links, or opacity changes
  const updateGraphWithCurrentSettings = useCallback(() => {
    if (allNodes.length === 0 || allLinks.length === 0) return;
    
    const linksWithUpdatedOpacity = updateLinkOpacity(
      allLinks,
      collectionLinkOpacity,
      tagLinkOpacity
    );
    
    // No filtering by selected node anymore, just use all nodes
    setGraphData({
      nodes: allNodes,
      links: linksWithUpdatedOpacity
    });
  }, [
    allNodes,
    allLinks,
    collectionLinkOpacity,
    tagLinkOpacity,
    updateLinkOpacity
  ]);

  useEffect(() => {
    updateGraphWithCurrentSettings();
  }, [
    allNodes,
    allLinks,
    collectionLinkOpacity,
    tagLinkOpacity,
    updateGraphWithCurrentSettings
  ]);

  // Calculate metrics
  const { collectionLinksCount, tagLinksCount } = useGraphMetrics(allLinks);

  return { 
    graphData, 
    boundingBox, 
    setBoundingBox,
    collectionLinksCount, 
    tagLinksCount 
  };
};
